import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import ImpersonationBanner from '@/components/ImpersonationBanner';
import { Toaster } from '@/components/ui/sonner';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import Home from '@/pages/Home';
import About from '@/pages/About';
import Services from '@/pages/Services';
import ExamPrep from '@/pages/ExamPrep';
import ForStudents from '@/pages/ForStudents';
import ForRecruiters from '@/pages/ForRecruiters';
import Contact from '@/pages/Contact';
import RecruiterSignUp from '@/pages/RecruiterSignUp';
import EnglishAssessment from '@/pages/EnglishAssessment';
import Login from '@/pages/Login';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import SignAgreement from '@/pages/SignAgreement';
import ChangePassword from '@/pages/ChangePassword';
import StudentApply from '@/pages/StudentApply';
import TrackApplication from '@/pages/TrackApplication';
import BookCall from '@/pages/BookCall';
import AdminDashboard from '@/pages/admin/Dashboard';
import RecruiterDashboard from '@/pages/recruiter/Dashboard';
import RecruiterPreAuthorization from '@/pages/recruiter/PreAuthorization';import InstitutionDashboard from '@/pages/institution/Dashboard';
import CreateStudent from '@/pages/recruiter/CreateStudent';
import RecruiterMessages from '@/pages/recruiter/Messages';
import RecruiterGuidelines from '@/pages/RecruiterGuidelines';

function ProtectedRoute({ role }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><div className="animate-pulse text-gray-400">Loading...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  // Force password change redirect
  if (user.must_change_password && window.location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  return <Outlet />;
}

function PublicLayout() {
  return (
    <>
      <Navbar />
      <main className="min-h-screen"><Outlet /></main>
      <Footer />
    </>
  );
}

function DashboardLayout() {
  return (
    <>
      <Navbar />
      <main><Outlet /></main>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <ImpersonationBanner />
        <Routes>
          {/* Standalone (no layout — white-labeled) */}
          <Route path="/english-assessment" element={<EnglishAssessment />} />

          {/* Public pages */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/exam-prep" element={<ExamPrep />} />
            <Route path="/for-students" element={<ForStudents />} />
            <Route path="/for-recruiters" element={<ForRecruiters />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/recruiter-signup" element={<RecruiterSignUp />} />
            <Route path="/login" element={<Login variant="recruiter" />} />
            <Route path="/admin/login" element={<Login variant="admin" />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/apply" element={<StudentApply />} />
            <Route path="/track-application" element={<TrackApplication />} />
            <Route path="/book/:leadId" element={<BookCall />} />
            <Route path="/call-appointment" element={<BookCall />} />
            <Route path="/sign-agreement/:recruiterId" element={<SignAgreement />} />
            <Route path="/recruiter-guidelines" element={<RecruiterGuidelines />} />
          </Route>

          {/* Admin */}
          <Route element={<DashboardLayout />}>
            <Route element={<ProtectedRoute role="admin" />}>
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/create-student" element={<CreateStudent />} />
              <Route path="/admin/student/:id" element={<CreateStudent />} />
            </Route>
          </Route>

          {/* Change Password (any authenticated user) */}
          <Route element={<DashboardLayout />}>
            <Route element={<ProtectedRoute />}>
              <Route path="/change-password" element={<ChangePassword />} />
            </Route>
          </Route>

          {/* Recruiter */}
          <Route element={<DashboardLayout />}>
            <Route element={<ProtectedRoute role="recruiter" />}>
              <Route path="/recruiter" element={<RecruiterDashboard />} />
              <Route path="/recruiter/pre-authorization" element={<RecruiterPreAuthorization />} />
              <Route path="/recruiter/messages" element={<RecruiterMessages />} />
              <Route path="/recruiter/create-student" element={<CreateStudent />} />
              <Route path="/recruiter/student/:id" element={<CreateStudent />} />
            </Route>
          </Route>

          {/* Institution portal — its own role guard */}
          <Route element={<DashboardLayout />}>
            <Route element={<ProtectedRoute role="institution" />}>
              <Route path="/institution" element={<InstitutionDashboard />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
